'use client';

// base
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

// styles
import { Column, Row } from 'styles';
import { StyleCheckingPassword } from '../ChagePassword/style';

// components
import { BasicModal, BasicLabel, TextButton, BasicPassword } from 'components';

// contexts
import { useAlert } from 'contexts';

// domains
import { useChangePasswordForce } from 'domains/Auth';

interface ChangePasswordProps {
  open: boolean;
  onClose: () => void;
  userId: string;
}

const passwordRegExp = {
  lowerCase: /[a-z]/,
  specialCharacter: /[!@#$%^&*(),.?":{}|<>]/,
  number: /[0-9]/,
  length: /.{8,}/,
  consecutive: /(.)\1{3,}/
};

type CheckingPasswordKey = keyof CheckingPassword;

interface PasswordMessageType {
  msg: string;
  type: CheckingPasswordKey;
}

const passwordMessage: PasswordMessageType[] = [
  {
    msg: 'Password must contain a lowercase letter',
    type: 'isLowerCase'
  },
  {
    msg: 'Password must contain a special character',
    type: 'isSpecialCharacter'
  },
  {
    msg: 'Password must contain a number',
    type: 'isNumber'
  },
  {
    msg: 'Password must contain at least 8 characters',
    type: 'isLength'
  },
  {
    msg: "Password Can't be included more than 4 consecutive identical characters",
    type: 'isConsecutive'
  },
  {
    msg: 'Password and Password Confirm are the same',
    type: 'isSame'
  }
];

interface CheckingPassword {
  isSpecialCharacter: boolean;
  isLowerCase: boolean;
  isNumber: boolean;
  isLength: boolean;
  isConsecutive: boolean;
  isSame: boolean;
}

export const ChangePasswordForce: React.FC<ChangePasswordProps> = ({ open, onClose, userId }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [checkingPassword, setCheckingPassword] = useState<CheckingPassword>({
    isSpecialCharacter: false,
    isLowerCase: false,
    isNumber: false,
    isLength: false,
    isConsecutive: false,
    isSame: false
  });
  const { mutateAsync: changePassword } = useChangePasswordForce();
  const { showAlert, showConfirmAlert } = useAlert();
  const router = useRouter();

  useEffect(() => {
    const isLowerCase = passwordRegExp.lowerCase.test(newPassword as string);
    const isSpecialCharacter = passwordRegExp.specialCharacter.test(newPassword as string);
    const isNumber = passwordRegExp.number.test(newPassword as string);
    const isLength = newPassword?.length >= 8;
    const isConsecutive = newPassword && !/(.)\1{3,}/.test(newPassword as string);
    const isSame = newPassword && confirmPassword && newPassword === confirmPassword;

    setCheckingPassword({
      isLowerCase: !!isLowerCase,
      isSpecialCharacter: !!isSpecialCharacter,
      isNumber: !!isNumber,
      isLength: !!isLength,
      isConsecutive: !!isConsecutive,
      isSame: !!isSame
    });
  }, [newPassword, confirmPassword]);

  return (
    <BasicModal
      open={open}
      width={500}
      height={630}
      onClose={onClose}
      header="Change Password"
      body={
        <Column gap="30px">
          <Column gap="20px">
            <Column gap="12px">
              <BasicLabel label={'Current password'} required />
              <BasicPassword
                placeholder="Enter Your Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </Column>

            <Column gap="12px">
              <BasicLabel label={'New Password'} required />
              <Row style={{ position: 'relative', alignItems: 'center' }}>
                <BasicPassword
                  placeholder="Enter Your Password"
                  style={{ flex: 1 }}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </Row>
            </Column>

            <Column gap="12px">
              <BasicLabel label={'Check the new Password'} required />
              <BasicPassword
                placeholder="Enter Your Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Column>

            <Column gap="8px">
              {passwordMessage.map((item) => (
                <StyleCheckingPassword
                  key={item.type}
                  className={checkingPassword[item.type] ? 'true' : ''}
                >
                  {item.msg}
                </StyleCheckingPassword>
              ))}
            </Column>
          </Column>

          <Row style={{ justifyContent: 'end', gap: '10px' }}>
            <TextButton label={'Cancel'} variant="outlined" onClick={onClose} />
            <TextButton
              label={'Save'}
              onClick={() => {
                if (newPassword !== confirmPassword) {
                  showAlert('Passwords do not match.');
                  return;
                }

                const failedValidation = Object.entries(checkingPassword).find(
                  ([_, value]) => value === false
                );

                if (failedValidation) {
                  const errorMessage = passwordMessage.find(
                    (msg) => msg.type === failedValidation[0]
                  )?.msg;

                  showAlert(`${errorMessage || 'Password validation failed'}`);
                  return;
                }

                showConfirmAlert('Would you like to proceed with the changes?', () => {
                  changePassword(
                    {
                      data: {
                        userId: userId,
                        currentPassword: currentPassword,
                        newPassword: newPassword
                      }
                    },
                    {
                      onSuccess: () => {
                        showAlert('it has been saved.', () => onClose());
                      },
                      onError: (error) => {
                        showAlert('The current password does not match');
                      }
                    }
                  );
                });
              }}
            />
          </Row>
        </Column>
      }
    />
  );
};
